import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { authClient, useSession } from '../auth-client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { EmptyState, ErrorNote, Field, FormGrid, PageHeader, Panel } from '../components/kit';

interface CommitIdentityRow {
  tenantId: string;
  tenant: string;
  slug: string;
  role: string;
  name: string | null;
  email: string | null;
}

/** Everything that belongs to the person signed in, rather than to a tenant. */
export function AccountPage() {
  const session = useSession();
  const user = session.data?.user;

  return (
    <>
      <PageHeader
        title="Account"
        description="Your name, your password, and who your runs commit as in each tenant."
      />

      <Panel title="You" description="The account you are signed in as.">
        {!user ? <Skeleton className="h-16 w-full" /> : <Profile name={user.name} email={user.email} />}
      </Panel>

      <Panel
        title="Password"
        description="Changing it signs out your other sessions — a password you had to change is one you no longer trust."
      >
        <ChangePassword />
      </Panel>

      <CommitIdentities />
    </>
  );
}

function Profile({ name, email }: { name: string; email: string }) {
  const [value, setValue] = useState(name);
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: async () => {
      const result = await authClient.updateUser({ name: value.trim() });
      if (result.error) throw new Error(result.error.message ?? 'could not save your name');
      return result;
    },
    onSuccess: () => setSaved(true),
  });

  return (
    <FormGrid
      onSubmit={(event) => {
        event.preventDefault();
        setSaved(false);
        save.mutate();
      }}
    >
      <Field label="Name" htmlFor="account-name" hint="How you appear on invitations you send.">
        <Input
          id="account-name"
          value={value}
          onChange={(event) => setValue(event.target.value)}
        />
      </Field>
      <Field
        label="Email"
        htmlFor="account-email"
        hint="Your sign-in address. An administrator changes it, because it is also how invitations reached you."
      >
        <Input id="account-email" value={email} disabled readOnly />
      </Field>
      <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
        <Button type="submit" disabled={save.isPending || value.trim() === name}>
          Save name
        </Button>
        {saved && <span className="text-[12px] text-ok">Saved.</span>}
        <ErrorNote error={save.error} />
      </div>
    </FormGrid>
  );
}

function ChangePassword() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [done, setDone] = useState(false);

  const change = useMutation({
    mutationFn: async () => {
      const result = await authClient.changePassword({
        currentPassword: current,
        newPassword: next,
        revokeOtherSessions: true,
      });
      if (result.error) throw new Error(result.error.message ?? 'could not change your password');
      return result;
    },
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setDone(true);
    },
  });

  return (
    <FormGrid
      onSubmit={(event) => {
        event.preventDefault();
        setDone(false);
        change.mutate();
      }}
    >
      <Field label="Current password" htmlFor="account-current">
        <Input
          id="account-current"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
        />
      </Field>
      <Field label="New password" htmlFor="account-next" hint="At least 12 characters.">
        <Input
          id="account-next"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(event) => setNext(event.target.value)}
        />
      </Field>
      <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
        <Button type="submit" disabled={change.isPending || current.length < 1 || next.length < 12}>
          Change password
        </Button>
        {done && <span className="text-[12px] text-ok">Changed. Other sessions were signed out.</span>}
        <ErrorNote error={change.error} />
      </div>
    </FormGrid>
  );
}

/**
 * One identity per tenant, in one place: work commits under a work address and
 * personal work does not, without hunting through each tenant's settings.
 */
function CommitIdentities() {
  const client = useQueryClient();
  const rows = useQuery({
    queryKey: ['commit-identities'],
    queryFn: () => api.get<CommitIdentityRow[]>('/api/me/commit-identities'),
  });

  return (
    <Panel
      title="Commit identities"
      description="Who your runs commit as, per tenant. Work a tracker raised always commits as the tenant — nobody queued it by hand — and so does work you queue in a tenant you have left empty."
    >
      {rows.isPending ? (
        <Skeleton className="h-24 w-full" />
      ) : (rows.data ?? []).length === 0 ? (
        <EmptyState
          title="You belong to no tenants yet"
          hint="An administrator grants access, or you accept an invitation."
        />
      ) : (
        <div className="divide-y divide-rule">
          {(rows.data ?? []).map((row) => (
            <IdentityRow
              key={row.tenantId}
              row={row}
              onSaved={() => client.invalidateQueries({ queryKey: ['commit-identities'] })}
            />
          ))}
        </div>
      )}
    </Panel>
  );
}

function IdentityRow({ row, onSaved }: { row: CommitIdentityRow; onSaved: () => void }) {
  const [form, setForm] = useState<{ name: string; email: string } | null>(null);
  const current = form ?? { name: row.name ?? '', email: row.email ?? '' };
  const dirty = current.name !== (row.name ?? '') || current.email !== (row.email ?? '');

  const save = useMutation({
    mutationFn: (values: { name: string | null; email: string | null }) =>
      api.put(`/api/tenants/${row.tenantId}/commit-identity`, values),
    onSuccess: () => {
      setForm(null);
      onSaved();
    },
  });

  return (
    <div className="py-3 first:pt-0 last:pb-0">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-semibold">{row.tenant}</span>
        <span className="data text-ink-3">{row.slug}</span>
        <Badge variant="secondary">{row.role}</Badge>
        {!row.name && !row.email && <Badge variant="outline">commits as the tenant</Badge>}
      </div>
      <form
        className="grid max-w-[60rem] gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)_11rem]"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate({ name: current.name.trim() || null, email: current.email.trim() || null });
        }}
      >
        <Input
          aria-label={`Commit author in ${row.tenant}`}
          placeholder="Author name"
          value={current.name}
          onChange={(event) => setForm({ ...current, name: event.target.value })}
        />
        <Input
          aria-label={`Commit email in ${row.tenant}`}
          type="email"
          placeholder="you@example.com"
          value={current.email}
          onChange={(event) => setForm({ ...current, email: event.target.value })}
        />
        <div className="flex gap-2">
          <Button type="submit" disabled={save.isPending || !dirty}>
            Save
          </Button>
          {(row.name || row.email) && (
            <Button
              type="button"
              variant="outline"
              disabled={save.isPending}
              onClick={() => save.mutate({ name: null, email: null })}
            >
              Clear
            </Button>
          )}
        </div>
        <div className="sm:col-span-3">
          <ErrorNote error={save.error} />
        </div>
      </form>
    </div>
  );
}
